import { getClientProductStock } from 'product/services/productStock/client/getClientProductStock'
import type { Product } from 'product/types'
import type { Stock } from 'product/types/Stock'
import type { PageParams } from 'types/pageParams'

import type { FullProduct } from '../../types/FullProduct'

interface GetProductInfoProps {
	productId: Product['id']
	country: PageParams['country']
	language: PageParams['language']
	enabled?: boolean
}

interface GetRelatedStock {
	getProductInfoProps: GetProductInfoProps
	productInfo: Product | FullProduct
	getStockFn: (relatedModelProps: GetProductInfoProps) => Promise<Stock | null>
}

export async function getClientRelatedStock(
	getProductInfoProps: GetProductInfoProps,
	productInfo: FullProduct
): Promise<Stock[]> {
	return getRelatedStock({
		getProductInfoProps,
		productInfo,
		getStockFn: getClientProductStock,
	})
}

export async function getRelatedStock({
	getProductInfoProps,
	productInfo,
	getStockFn,
}: GetRelatedStock): Promise<Stock[]> {
	const relatedProductIds = getClientRelatedProductIds(productInfo)

	if (relatedProductIds.length > 0) {
		const relatedModelsPromises = relatedProductIds.map((id) => {
			const relatedModelProps: GetProductInfoProps = {
				...getProductInfoProps,
				productId: id,
			}

			return getStockFn(relatedModelProps)
		})

		const promises = await Promise.allSettled(relatedModelsPromises)

		return promises.reduce<Stock[]>((acc, current) => {
			if (current.status === 'fulfilled' && current.value) {
				acc.push(current.value)
			}
			return acc
		}, [])
	}

	return []
}

export function getClientRelatedProductIds(
	productInfo: Product | FullProduct
): string[] {
	const relatedProductSizeIds = productInfo.colors.reduce<string[]>(
		(acc, color) => {
			if (color.relatedProductId) {
				acc.push(color.relatedProductId)
			}
			color.sizes.forEach((size) => {
				if (size.relatedProductId) {
					acc.push(size.relatedProductId)
				}
			})
			return acc
		},
		[]
	)

	return Array.from(new Set(relatedProductSizeIds))
}
